/**
 * Resolve dependency / coverage rows to an editor-openable metadata id.
 *
 * COPIED from pages/dev-agent/components/rightPanels/resolveDependencyItemForEditor.ts.
 * The by-name / by-id metadata lookups now resolve NATIVELY against the
 * multi-agent backend (MULTI_AGENT_API_BASE_URL) on the same paths
 * (`/api/chat/metadata/items/{id}` and `.../by-name/...`) instead of the
 * dev-agent proxy. That keeps every import inside this file pointed at shared
 * utils only — no edge into pages/dev-agent.
 */
import { createAxiosInstance } from "@/dataProvider/axios";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getArtifact, getMultiAgentAuthHeaders, isHexDigit, MULTI_AGENT_API_BASE_URL } from "@/services/multi-agent";

/**
 * Canonical UUID: 8-4-4-4-12 hex digits (case-insensitive) with hyphens at
 * positions 8/13/18/23 and a total length of 36.
 */
function _isCanonicalUuid(id: string): boolean {
  if (id.length !== 36) return false;
  for (let i = 0; i < 36; i++) {
    const ch = id[i];
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      if (ch !== "-") return false;
    } else if (!isHexDigit(ch)) {
      return false;
    }
  }
  return true;
}

export type DependencyOpenItem = {
  name: string;
  id?: string;
  metadata_id?: string;
  metadata_type?: string;
  metadataType?: string;
};

export interface ResolvedMetadataItem {
  id: string;
  full_name?: string;
  name?: string;
  metadata_type?: string;
}

/** Resolved item including content/language — for Compare baselines. */
export interface ResolvedMetadataItemFull extends ResolvedMetadataItem {
  content?: string;
  language?: string | null;
}

export function artifactTypeToMetadataType(artifactType: string): string {
  if (artifactType === "apex_trigger") return "ApexTrigger";
  if (artifactType === "visualforce_page") return "ApexPage";
  if (
    artifactType === "lwc_bundle" ||
    artifactType === "lwc_html" ||
    artifactType === "lwc_js"
  ) {
    return "LightningComponentBundle";
  }
  if (artifactType === "aura_bundle") return "AuraDefinitionBundle";
  if (artifactType === "flow_definition") return "Flow";
  return "ApexClass";
}

// Metadata items (by-id / by-name) AND artifacts resolve natively against the
// multi-agent backend (org/user-scoped) — no dev-agent proxy.
const metadataApi = createAxiosInstance(MULTI_AGENT_API_BASE_URL);

function unwrap<T>(data: any): T {
  return data?.data && data?.status ? (data.data as T) : (data as T);
}

function withIdentityParams(orgId?: string): string {
  const userId = useAuthStoreV1.getState().user?.id || "";
  const params = new URLSearchParams();
  const resolvedOrgId = orgId || useAuthStoreV1.getState().selectedOrg?.id || "";
  if (resolvedOrgId) params.set("org_id", resolvedOrgId);
  if (userId) params.set("user_id", userId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Shared by-name metadata lookup against the agentic-platform backend. Mirrors
 * the dev-agent `getMetadataItemByName` shape so the copied panels behave
 * identically, but lives here so the multi-agent panels never import dev-agent.
 */
export async function getMetadataItemByName(
  name: string,
  orgId?: string,
  silent = false,
): Promise<ResolvedMetadataItem> {
  const params = withIdentityParams(orgId);
  return unwrap<ResolvedMetadataItem>(
    (
      await metadataApi.get(
        `/api/chat/metadata/items/by-name/${encodeURIComponent(name)}${params}`,
        { silent, headers: getMultiAgentAuthHeaders() } as any,
      )
    ).data,
  );
}

/**
 * Resolve a metadata item BY ID — the dev-agent `getMetadataItem` pattern.
 * Routes on the id shape: `art_…` → the artifact endpoint; a canonical UUID →
 * the metadata-item endpoint. Never resolves by name.
 */
export async function getMetadataItem(
  id: string,
  orgId?: string,
  silent = false,
): Promise<ResolvedMetadataItemFull> {
  const params = withIdentityParams(orgId);
  const fromArtifact = (artifact: Awaited<ReturnType<typeof getArtifact>>): ResolvedMetadataItemFull => ({
    id: artifact.id,
    full_name: artifact.name || artifact.id,
    name: artifact.name || artifact.id,
    metadata_type: artifactTypeToMetadataType(artifact.type || ""),
    content: artifact.content || "",
    language: artifact.language || undefined,
  });

  if (isArtifactSessionId(id)) {
    // Dev-agent artifacts (`art_…`) resolve through the artifact endpoint.
    return fromArtifact(await getArtifact(id));
  }

  try {
    return unwrap<ResolvedMetadataItemFull>(
      (
        await metadataApi.get(
          `/api/chat/metadata/items/${encodeURIComponent(id)}${params}`,
          { silent, headers: getMultiAgentAuthHeaders() } as any,
        )
      ).data,
    );
  } catch (metaErr) {
    // Multi-agent artifacts use BARE UUIDs (not `art_…`) and live in the
    // artifacts table, NOT metadata_items — so the metadata-item endpoint 404s
    // (the dev-agent code never hit this because its artifact ids are prefixed).
    // Replicate dev-agent's intent — "an artifact resolves via the artifact
    // endpoint" — by retrying the SAME id against /api/artifacts before failing.
    try {
      return fromArtifact(await getArtifact(id));
    } catch {
      throw metaErr;
    }
  }
}

export function normalizeDependencyOpenItem(raw: DependencyOpenItem): DependencyOpenItem {
  return {
    name: (raw.name || "").trim(),
    id: String(raw.id || raw.metadata_id || "").trim(),
    metadata_type: raw.metadata_type || raw.metadataType,
  };
}

export function isCanonicalMetadataItemId(id: string): boolean {
  return _isCanonicalUuid(id);
}

export function isArtifactSessionId(id: string): boolean {
  return id.startsWith("art_");
}

/** Tab / GraphQL keys that do not need a by-name lookup (UUID or session artifact). */
export function isEditorSafeMetadataItemId(id: string | undefined | null): boolean {
  if (!id) return false;
  return isCanonicalMetadataItemId(id) || isArtifactSessionId(id);
}

/**
 * Returns a canonical metadata UUID or `art_…` id for `onOpenFileInEditor`.
 * Salesforce 15/18-char ids, class names, and stale ids fall back to by-name lookup.
 */
export async function resolveDependencyItemForEditor(
  raw: DependencyOpenItem,
  orgId: string | undefined,
): Promise<{ id: string; name: string; metadataType: string } | null> {
  const item = normalizeDependencyOpenItem(raw);
  const name = item.name;
  if (!name) return null;

  let itemId = item.id || "";

  if (!isEditorSafeMetadataItemId(itemId)) {
    if (!orgId) return null;
    try {
      const resolved = await getMetadataItemByName(name, orgId, true);
      itemId = resolved?.id || "";
    } catch {
      return null;
    }
  }

  if (!itemId) return null;

  return {
    id: itemId,
    name,
    metadataType: item.metadata_type || "ApexClass",
  };
}

export function isSameDependencySnapshot(
  a: { target: string; captured_at?: string },
  b: { target: string; captured_at?: string },
): boolean {
  if (a.target !== b.target) return false;
  if (a.captured_at && b.captured_at) return a.captured_at === b.captured_at;
  return true;
}
